import { Badge } from "@/components/ui/badge";
import { User, FileText, Users, FileCheck } from "lucide-react";
import type { GroupSummary } from "../types.js";

interface SidebarGroupItemProps {
	group: GroupSummary;
	isSelected: boolean;
	isMinimized: boolean;
	onSelect: () => void;
}

/**
 * SidebarGroupItem component for displaying a group in the sidebar, opens the group details page when clicked
 * @param group - The group to display
 * @param isSelected - Whether the group is selected
 * @param isMinimized - Whether the group is minimized
 * @param onSelect - The function to call when the group is selected
 */
export function SidebarGroupItem({
	group,
	isSelected,
	isMinimized,
	onSelect,
}: SidebarGroupItemProps) {
	const getCompletionColor = (percentage: number) => {
		if (percentage >= 80) return "bg-green-100 text-green-900 border-green-200";
		if (percentage >= 50)
			return "bg-yellow-100 text-yellow-900 border-yellow-200";
		return "bg-red-100 text-red-900 border-red-200";
	};

	return (
		<button
			className={`
				relative w-full text-left border rounded-xl ${isMinimized ? "p-3" : "p-4"} cursor-pointer transition-all duration-200 group hover:shadow-md hover:-translate-y-0.5
				${
					isSelected
						? "border-primary bg-gradient-to-br from-primary/8 to-primary/4 shadow-md ring-1 ring-primary/20"
						: "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/50"
				}
			`}
			onClick={onSelect}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					onSelect();
				}
			}}
			tabIndex={0}
			type="button"
		>
			{/* Header with title and actions */}
			<div className="flex items-start justify-between mb-3">
				<div className="flex-1 min-w-0">
					<h3
						className={`font-semibold ${isMinimized ? "text-sm" : "text-base"} truncate transition-colors ${
							isSelected ? "text-primary" : "text-slate-900"
						}`}
					>
						{group.name}
					</h3>
					{!isMinimized && (
						<p className="text-xs text-slate-500 mt-0.5">
							{group.userCount} Nutzer*innen • {group.examCount} Klausuren
						</p>
					)}
				</div>
			</div>

			{/* Stats row for minimized view */}
			{isMinimized && (
				<div className="flex items-center gap-3 mb-2 text-xs text-slate-600">
					<div className="flex items-center gap-1">
						<User className="h-3 w-3" />
						<span className="font-medium">{group.userCount}</span>
					</div>
					<div className="flex items-center gap-1">
						<FileText className="h-3 w-3" />
						<span className="font-medium">{group.examCount}</span>
					</div>
				</div>
			)}

			{/* Progress Section */}
			<div className={isMinimized ? "flex gap-2" : "space-y-3"}>
				{/* Assignment Progress */}
				<div className={isMinimized ? "flex-1" : "space-y-1"}>
					<div className="flex items-center justify-between">
						{!isMinimized && (
							<span className="text-xs font-medium text-slate-600 uppercase tracking-wide">
								Zuweisungen
							</span>
						)}
						<Badge
							variant="outline"
							className={`${isMinimized ? "w-full justify-center" : ""} gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-lg border-2 transition-all ${getCompletionColor(group.completionPercentage)}`}
							title={
								isMinimized
									? `Zuweisungen: ${group.completionPercentage}%`
									: undefined
							}
						>
							<Users className={isMinimized ? "h-3 w-3" : "h-3.5 w-3.5"} />
							{group.completionPercentage}%
						</Badge>
					</div>
					{!isMinimized && (
						<div className="w-full bg-slate-200 rounded-full h-1.5 mt-2 overflow-hidden">
							<div
								className={`h-full transition-all duration-300 ${
									group.completionPercentage >= 80
										? "bg-green-500/70"
										: group.completionPercentage >= 50
											? "bg-yellow-500/70"
											: "bg-red-600/70"
								}`}
								style={{ width: `${group.completionPercentage}%` }}
							/>
						</div>
					)}
				</div>
				{/* Correction Progress */}
				<div className={isMinimized ? "flex-1" : "space-y-1"}>
					<div className="flex items-center justify-between">
						{!isMinimized && (
							<span className="text-xs font-medium text-slate-600 uppercase tracking-wide">
								Korrekturen
							</span>
						)}
						<Badge
							variant="outline"
							className={`${isMinimized ? "w-full justify-center" : ""} gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-lg border-2 transition-all ${getCompletionColor(group.correctionPercentage)}`}
							title={
								isMinimized
									? `Korrekturen: ${group.correctionPercentage}%`
									: undefined
							}
						>
							<FileCheck className={isMinimized ? "h-3 w-3" : "h-3.5 w-3.5"} />
							{group.correctionPercentage}%
						</Badge>
					</div>
					{!isMinimized && (
						<div className="w-full bg-slate-200 rounded-full h-1.5 mt-2 overflow-hidden">
							<div
								className={`h-full transition-all duration-300 ${
									group.correctionPercentage >= 80
										? "bg-green-500/70"
										: group.correctionPercentage >= 50
											? "bg-yellow-500/70"
											: "bg-red-600/70"
								}`}
								style={{ width: `${group.correctionPercentage}%` }}
							/>
						</div>
					)}
				</div>
			</div>
		</button>
	);
}
